import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import PublicLayout from "../components/PublicLayout";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { CheckCircle2, Phone, MapPin, Calendar, Users, Bus, Clock8 } from "lucide-react";
import { toast } from "sonner";
import { useT } from "../lib/i18n";

export default function BookingConfirmation() {
  const t = useT();
  const { bookingId } = useParams();
  const [b, setB] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(()=>{ api.get(`/bookings/${bookingId}`).then(r=>setB(r.data)).catch(()=>{}); }, [bookingId]);

  const pay = async () => {
    setPaying(true);
    try {
      const r = await api.post("/payments/checkout", { booking_id: bookingId, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Payment start failed"); setPaying(false); }
  };

  if (!b) return <PublicLayout><div className="max-w-3xl mx-auto p-10">{t("conf.loading")}</div></PublicLayout>;

  const thankYou = t("conf.thank_you").replace("{name}", b.customer_name || "");

  return (
    <PublicLayout>
      <section className="max-w-3xl mx-auto px-4 pt-10 pb-16">
        <div className="p-8 rounded-2xl bg-white border border-emerald-200 shadow-sm">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-10 h-10 text-emerald-600" />
            <div>
              <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">{t("conf.booking_confirmed")}</div>
              <h1 className="text-2xl md:text-3xl font-display font-black tracking-tight">{thankYou}</h1>
            </div>
          </div>
          <div className="mt-4 p-4 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-between">
            <div><div className="text-xs text-slate-500">{t("conf.booking_id")}</div><div className="font-display font-black text-xl text-[#1e1b4b]" data-testid="conf-booking-id">{b.booking_id}</div></div>
            <div className="text-right"><div className="text-xs text-slate-500">{t("conf.total")}</div><div className="text-2xl font-black text-[#ea580c]">₹{b.fare.toLocaleString()}</div></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mt-6 text-sm">
            <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-[#ea580c] mt-0.5" /><div><div className="text-slate-500 text-xs">{t("conf.route")}</div>{b.from_place} → {b.to_place}</div></div>
            <div className="flex items-start gap-2"><Calendar className="w-4 h-4 text-[#ea580c] mt-0.5" /><div><div className="text-slate-500 text-xs">{t("conf.travel_date")}</div>{b.travel_date} {b.departure_time && `· ${b.departure_time}`}</div></div>
            <div className="flex items-start gap-2"><Users className="w-4 h-4 text-[#ea580c] mt-0.5" /><div><div className="text-slate-500 text-xs">{t("conf.passengers")}</div>{b.passengers} · {b.ac?t("bf.ac"):t("bf.nonac")}</div></div>
            <div className="flex items-start gap-2"><Bus className="w-4 h-4 text-[#ea580c] mt-0.5" /><div><div className="text-slate-500 text-xs">{t("conf.vehicle")}</div>{b.vehicle?.vehicle_number || b.vehicle_type_pref || t("conf.to_be_assigned")}</div></div>
            {b.pickup_location && <div className="flex items-start gap-2 sm:col-span-2"><MapPin className="w-4 h-4 text-[#ea580c] mt-0.5" /><div><div className="text-slate-500 text-xs">{t("conf.pickup")}</div>{b.pickup_location}</div></div>}
          </div>
          {b.status && <div className="mt-4 text-xs px-2.5 py-1 rounded-full bg-slate-100 inline-block font-medium capitalize">{t("conf.status")}: {b.status.replace(/_/g,' ')}</div>}
          <div className="mt-6 flex flex-wrap gap-3">
            {b.payment_status !== "paid" && (
              <Button onClick={pay} disabled={paying} className="bg-[#ea580c] hover:bg-orange-700" data-testid="pay-now-btn">{paying?t("conf.redirecting"):t("conf.pay_online")}</Button>
            )}
            <a href="tel:+919890201047" className="inline-flex items-center gap-1 px-4 py-2 rounded-md border border-slate-300 text-sm font-semibold hover:bg-slate-50" data-testid="call-support"><Phone className="w-4 h-4" /> {t("conf.call_support")}</a>
            <Link to="/lookup" className="inline-flex items-center px-4 py-2 rounded-md border border-slate-300 text-sm font-semibold hover:bg-slate-50" data-testid="track-booking-link">{t("conf.track_booking")}</Link>
          </div>
        </div>
        <div className="mt-4 p-4 rounded-xl bg-amber-50 border-l-4 border-amber-500 flex items-start gap-3" data-testid="early-arrival-notice">
          <Clock8 className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-display font-bold text-amber-900">{t("conf.arrive_early")}</div>
            <div className="text-sm text-amber-800/90 mt-0.5">{t("conf.arrive_early_body")}</div>
          </div>
        </div>
        <div className="mt-4 text-xs text-slate-500">{t("conf.wa_queued")}</div>
      </section>
    </PublicLayout>
  );
}
